import React, { useEffect, useMemo, useState } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { useStore } from '@mcn-platform/models';
import BulletPoints from 'components/forms/bulletItemsInput/BulletItems';

const normalizeLocales = (locales: Array<string | null | undefined> | undefined): string[] => {
  const cleaned = (locales || []).filter((locale) => Boolean(locale)) as string[];
  return cleaned.length > 0 ? cleaned : ['en'];
};

const LocalizedTextArrayControl = ({
  label,
  value,
  onChange,
  disabled,
  placeholder = 'Add prompt...',
}: {
  label: string;
  value: Record<string, string> | undefined;
  onChange: (newValue: Record<string, string> | undefined) => void;
  disabled: boolean;
  placeholder?: string;
}) => {
  const store = useStore();
  const supportedLocales = useMemo(
    () => normalizeLocales(store.currentPortal?.supportedLocales),
    [store.currentPortal?.supportedLocales],
  );
  const [selectedLocale, setSelectedLocale] = useState<string>(supportedLocales[0] || 'en');

  useEffect(() => {
    if (!supportedLocales.includes(selectedLocale)) {
      setSelectedLocale(supportedLocales[0] || 'en');
    }
  }, [supportedLocales, selectedLocale]);

  // BulletPoints uses string with splitSeparator, not array
  const currentValue = value?.[selectedLocale] || '';

  const handleValueChange = (newStringValue: string) => {
    const nextValue = { ...(value || {}) };
    if (newStringValue.trim()) {
      nextValue[selectedLocale] = newStringValue;
    } else {
      delete nextValue[selectedLocale];
    }
    if (Object.keys(nextValue).length === 0) {
      onChange(undefined);
      return;
    }
    onChange(nextValue);
  };

  return (
    <Stack spacing={1}>
      <FormControl size="small" fullWidth>
        <InputLabel>{label}</InputLabel>
        <Select
          value={selectedLocale}
          onChange={(event) => setSelectedLocale(event.target.value)}
          disabled={disabled}
          label={label}
        >
          {supportedLocales.map((locale) => (
            <MenuItem key={locale} value={locale}>
              {locale}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <BulletPoints
        value={currentValue}
        setValue={handleValueChange}
        placeholder={placeholder}
        disabled={disabled}
        minItems={1}
      />
    </Stack>
  );
};

LocalizedTextArrayControl.defaultProps = {
  placeholder: 'Add prompt...',
};

export default LocalizedTextArrayControl;
